import { formatCurrency } from '@if/core/utils'
import { DocumentField, EnumChip, Help, Icon, SummaryCard, TooltipIconButton } from '@if/mui'
import { Stack } from '@mui/material'
import { EMetro2AccountStatus } from '@src/types/constants'
import { Metro2Reporting } from '@src/types/loan/loan-calendar'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { CalendarCog, FileChartColumn } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  useLoanCalendarActivationOn,
  useLoanCalendarMetro2Reportings,
  useLoanSummaryInformation,
} from '../LoanCalendar-hooks'
import { useLoanCalendarSimulation } from '../LoanCalendarSimulationProvider'
import { EMetro2AccountStatusSet, toMetro2AccountStatusValue } from '../metro2-account-status'
import { useMetro2ReportingDialog } from '../Metro2Reporting/Metro2ReportingDialog'

interface Props {
  signedSimulation: SignedCalendarSimulation
}

const getCurrentMetro2Reporting = (metro2Reportings: Metro2Reporting[] | null): Metro2Reporting | null => {
  if (!metro2Reportings || metro2Reportings?.length === 0) return null
  const latestMetro2Reporting: Metro2Reporting | null = metro2Reportings.reduce((prev, current) => {
    return prev.createdOn > current.createdOn ? prev : current
  })
  return latestMetro2Reporting || null
}

const LoanCreditReportCard = ({ signedSimulation }: Props) => {
  const { t } = useTranslation()
  const { addSimulationAction } = useLoanCalendarSimulation()
  const simulation = signedSimulation.simulation
  const metro2ReportingDialog = useMetro2ReportingDialog()

  const summaryInfo = useLoanSummaryInformation(simulation)
  const metro2LateAmount = summaryInfo?.metro2LateAmount ?? 0
  const metro2Reportings = useLoanCalendarMetro2Reportings(simulation)
  const metro2CurrentReporting = getCurrentMetro2Reporting(metro2Reportings)
  const activationOn = useLoanCalendarActivationOn(simulation)

  const openMetro2ReportingDialog = () => {
    void metro2ReportingDialog.open({ signedSimulation }).then((refreshedSimulation) => {
      if (refreshedSimulation) addSimulationAction(refreshedSimulation)
    })
  }

  return (
    <SummaryCard icon={FileChartColumn} title={t('loan.payment.creditReportCard.title')} dense>
      <DocumentField
        label={t('loan.payment.creditReportCard.lateStartDate')}
        align="right"
        value={
          <Stack component="span" direction="row" spacing={0.5} alignItems="center">
            <TooltipIconButton
              title={t('loan.payment.creditReportCard.changeLateStartDate')}
              size="small"
              onClick={openMetro2ReportingDialog}
            >
              <Icon icon={CalendarCog} size="sm" color="primary" />
            </TooltipIconButton>
            <span>
              {metro2CurrentReporting ? metro2CurrentReporting.metro2OverrideDate.toString() : activationOn.toString()}
            </span>
          </Stack>
        }
        strong={Boolean(metro2CurrentReporting)}
      />
      <DocumentField
        label={
          <Stack component="span" direction="row" spacing={0.5} alignItems="center">
            <span>{t('loan.payment.creditReportCard.firstDelinquencyDate')}</span>
            <Help title={t('loan.payment.creditReportCard.dateFirstDelinquencyDetails')} />
          </Stack>
        }
        align="right"
        value={
          summaryInfo?.metro2DateFirstDelinquency ? summaryInfo.metro2DateFirstDelinquency.toString() : t('common.none')
        }
      />
      <DocumentField
        label={t('loan.payment.creditReportCard.accountStatus')}
        align="right"
        value={
          <EnumChip
            set={EMetro2AccountStatusSet}
            value={toMetro2AccountStatusValue(summaryInfo?.metro2AccountStatus ?? EMetro2AccountStatus.Current)}
            size="small"
          />
        }
      />
      <DocumentField
        label={t('loan.payment.creditReportCard.lateAmount')}
        align="right"
        value={formatCurrency(metro2LateAmount)}
        valueColor={metro2LateAmount > 0 ? 'error' : undefined}
      />
      <DocumentField
        label={t('loan.payment.creditReportCard.reportingDate')}
        align="right"
        value={summaryInfo?.metro2DateReporting ? summaryInfo.metro2DateReporting.toString() : t('common.none')}
      />
    </SummaryCard>
  )
}

export default LoanCreditReportCard
